<template>
  <div v-if="reservation">
    <b-row>
      <b-col cols="12" md="6">
        <b-table-simple striped bordered>
          <b-tbody>
            <b-tr>
              <b-td>Booking Number</b-td>
              <b-td class="d-flex justify-content-between">
                <span>#{{ reservation.id }}</span>
                <b-button
                  @click="editMode = true"
                  variant="success"
                  v-if="!editMode"
                >
                  <i class="ri-ball-pen-fill"></i>
                </b-button>
              </b-td>
            </b-tr>
            <b-tr v-if="editMode">
              <b-td colspan="2">
                <date-picker
                  v-model="calendarDates"
                  range
                  @input="data => updatedDates(data)"
                ></date-picker>
              </b-td>
            </b-tr>
            <b-tr>
              <b-td colspan="2">
                From
                <span class="text-danger">{{
                  moment(reservation.from).format("ddd, Do MMM")
                }}</span>
                To
                <span class="text-danger">{{
                  moment(reservation.to).format("ddd, Do MMM")
                }}</span>
              </b-td>
            </b-tr>
            <b-tr>
              <b-td>Booked at</b-td>
              <b-td>{{
                moment(reservation.created_at).format("YYYY-MM-DD hh:mm A")
              }}</b-td>
            </b-tr>
            <b-tr>
              <b-td>Booked By</b-td>
              <b-td>{{ reservation.data.reserved_by.name }}</b-td>
            </b-tr>
            <b-tr>
              <b-td>Platform</b-td>
              <b-td v-if="reservation.order_id !== null" class="text-primary">APP <i class="ri-smartphone-line"></i> </b-td>
              <b-td v-else  class="text-primary">Admin <i class="ri-computer-line"></i> </b-td>
            </b-tr>
            <b-tr>
              <b-td>Status</b-td>
              <b-td v-if="editMode">
                <b-form-select v-model="updatedReservation.status">
                  <b-form-select-option :value="ReservationStatuses.pending">
                    {{ ReservationStatuses.pending }}
                  </b-form-select-option>
                  <b-form-select-option :value="ReservationStatuses.completed">
                    {{ ReservationStatuses.completed }}
                  </b-form-select-option>
                  <b-form-select-option :value="ReservationStatuses.canceled">
                    {{ ReservationStatuses.canceled }}
                  </b-form-select-option>
                </b-form-select>
              </b-td>
              <b-td
                v-else
                class="text-uppercase"
                :style="{
                  color: StatusColors[reservation.payment_status || 'no_data']
                }"
              >
                {{ reservation.status }}
              </b-td>
            </b-tr>
            <b-tr>
              <b-td>Payment Status</b-td>
              <b-td
                class="text-uppercase"
                :style="{
                  color: StatusColors[reservation.payment_status || 'no_data']
                }"
              >
                {{ reservation.payment_status || "no_data" }}
              </b-td>
            </b-tr>
          </b-tbody>
        </b-table-simple>

        <b-table-simple striped bordered>
          <b-tbody>
            <template v-if="editMode">
              <b-tr>
                <b-td>Current Chalet</b-td>
                <b-td>
                  {{ reservationNameWithId }}
                </b-td>
              </b-tr>
              <b-tr>
                <b-td colspan="3">
                  <SearchItemsForm
                    :business-id="businessId"
                    ref="SearchItemsForm"
                    :branch-id="branchId"
                    label="Search chalet"
                    @setItem="
                      itemDetails =>
                        (updatedReservation.reservable_id =
                          itemDetails.id || null)
                    "
                  />
                </b-td>
              </b-tr>
            </template>
            <template v-else>
              <b-tr>
                <b-td>Chalet name</b-td>
                <b-td>
                  {{ reservationNameWithId }}
                </b-td>
              </b-tr>
              <b-tr v-if="reservation.data.reservable.itemable">
                <b-td>Address</b-td>
                <b-td>{{ getAddress() }}</b-td>
              </b-tr>
              <b-tr
                v-if="
                  reservation.data.reservable.itemable &&
                    reservation.data.reservable.itemable.latitude
                "
              >
                <b-td>Google maps link</b-td>
                <b-td>
                  <a
                    target="_blank"
                    class=" text-primary"
                    :href="googleMapLink()"
                    ><i class="ri-map-pin-line"></i> Maps Location
                  </a>
                </b-td>
              </b-tr>
            </template>
          </b-tbody>
        </b-table-simple>
      </b-col>

      <b-col cols="12" md="6">
        <div class="img img-thumbnail" style="margin-bottom: 1rem" v-if="reservable">
          <template v-if="reservable.media">
            <template v-for="(mediaItem, index) in reservable.media">
              <b-img
                  v-if=" mediaItem.type.search('image') !== -1 " 
                :key="index"
                height="120"
                class="mx-1"
                :src="__assetDimensioned(mediaItem.src, 250)"
                @error="handleImageError"
              />
            </template>
            
          </template>
        </div>

        <b-table-simple striped bordered v-if="reservation.data.reserved_for">
          <b-tbody>
            <b-tr>
              <b-th colspan="2">Guest Details</b-th>
            </b-tr>
            <tr v-if="editMode">
              <td colspan="2">
                <SearchUsersForm
                  ref="SearchUsersForm"
                  :businessId="businessId"
                  :reservation-errors="null"
                  :show-add-user="false"
                  @setUser="
                    user =>
                      (updatedReservation.reserved_for_id = user
                        ? user.id
                        : reservation.data.reserved_for.id)
                  "
                />
              </td>
            </tr>
            <template v-else>
              <b-tr>
                <b-td>Name</b-td>
                <b-td>{{ reservation.data.reserved_for.name }}</b-td>
              </b-tr>
              <b-tr v-if="reservation.data.reserved_for.email">
                <b-td>Email</b-td>
                <b-td>{{ reservation.data.reserved_for.email }}</b-td>
              </b-tr>
              <b-tr v-if="reservation.data.reserved_for.phone">
                <b-td>Phone</b-td>
                <b-td>{{ reservation.data.reserved_for.phone }}</b-td>
              </b-tr>
            </template>
          </b-tbody>
        </b-table-simple>

        <b-table-simple striped bordered v-if="reservation.data.reserved_for">
          <b-tbody>
            <b-tr>
              <b-th colspan="2">Followed by</b-th>
            </b-tr>
            <b-tr v-if="editMode">
              <b-td colspan="2">
                <SearchUsersForm
                  ref="FollowedByForm"
                  :businessId="businessId"
                  :reservation-errors="null"
                  :show-add-user="false"
                  @setUser="
                    user =>
                      (updatedReservation.follower_id = user
                        ? user.id
                        : reservation.follower_id)
                  "
                />
              </b-td>
            </b-tr>
            <template v-else-if="reservation.data.follower">
              <b-tr>
                <b-td>Name</b-td>
                <b-td>{{ reservation.data.follower.name }}</b-td>
              </b-tr>
              <b-tr v-if="reservation.data.follower.email">
                <b-td>Email</b-td>
                <b-td>{{ reservation.data.follower.email }}</b-td>
              </b-tr>
              <b-tr v-if="reservation.data.follower.phone">
                <b-td>Phone</b-td>
                <b-td>{{ reservation.data.follower.phone }}</b-td>
              </b-tr>
            </template>
            <template v-else>
              <b-tr>
                <b-td colspan="2">
                  <b-alert variant="warning" class="alert-outline" show
                    >No follower assigned
                  </b-alert>
                </b-td>
              </b-tr>
            </template>
          </b-tbody>
        </b-table-simple>
      </b-col>
    </b-row>

    <b-row>
      <b-col cols="12" md="12">
        <NotesList
          :notes-data="reservation.notes"
          @updateNotes="notes => updateNotes(notes)"
        />
      </b-col>
    </b-row>

    <b-row>
      <b-col cols="12" md="12">
        <InvoicesList
          :invoices-data="reservation.data.invoices"
          :businessId="businessId"
          :branchId="branchId"
          :reservationId="reservation.id"
          @updateInvoices="data => updateInvoices(data)"
        />
      </b-col>
    </b-row>
    <b-spinner v-if="loading" variant="success" label="Spinning"></b-spinner>

    <button
      v-else
      class="btn btn-success pull-end"
      type="button"
      @click="editReservation"
    >
      <i class="fa fa-save"></i> Save Updates
    </button>
  </div>
</template>

<script>
import moment from "moment";
import { StatusColors } from "@/API/BusinessAPIs";
import { ReservationStatuses } from "@/config/constant";
import DatePicker from "vue2-datepicker";
import "vue2-datepicker/index.css";
import {
  InvoicePaymentType,
  InvoiceStatus,
  InvoiceType
} from "@/config/Invoices";
import { __assetDimensioned } from "@/helpers/helpers";
import { updateReservation } from "@/API/ReservationsAPIs";
import SearchItemsForm from "./SearchItemsForm.vue";
import SearchUsersForm from "./SearchUsersForm.vue";
import NotesList from "./NotesList.vue";
import InvoicesList from "./InvoicesList.vue";
import { submitAndNotify } from "@/helpers/utils";
import { getItemData } from "@/API/ItemsAPIs";
export default {
  name: "ReservationDetails",
  components: {
    DatePicker,
    SearchItemsForm,
    SearchUsersForm,
    NotesList,
    InvoicesList
  },
  props: ["businessId", "branchId", "reservation"],
  data: function() {
    return {
      loading: false,
      moment: moment,
      editMode: false,
      updatedReservation: {},
      calendarDates: [],
      businessLogo: null,
      reservable: null
    };
  },
  computed: {
    currentLocale() {
      return this.$i18n.locale;
    },
    reservationNameWithId() {
      const reservable = this.reservation?.data?.reservable;

      const name =
        reservable?.locales?.find(
          locale => locale.locale === this.currentLocale
        )?.name || "No Name";
      return `${name}`;
    },
    ReservationStatuses() {
      return ReservationStatuses;
    },
    StatusColors() {
      return StatusColors;
    },
    InvoicePaymentType() {
      return InvoicePaymentType;
    },
    InvoiceStatus() {
      return InvoiceStatus;
    },
    InvoiceType() {
      return InvoiceType;
    }
  },
  async mounted() {
    this.updatedReservation.from = moment(this.reservation.from).format(
      "YYYY-MM-DDTHH:mm"
    );
    this.updatedReservation.to = moment(this.reservation.to).format(
      "YYYY-MM-DDTHH:mm"
    );
    this.updatedReservation.status = this.reservation.status;
    this.calendarDates = [
      new Date(this.updatedReservation.from),
      new Date(this.updatedReservation.to)
    ];
    await this.getReservableMedia();
  },
  methods: {
    __assetDimensioned,
    handleImageError(event) {
      event.target.src = "";
    },
    googleMapLink() {
      let item = this.reservation.data.reservable.itemable;
      return `https://maps.google.com/?q=${item?.latitude},${item?.longitude}`;
    },
    getAddress() {
      return this.reservation.data.reservable.itemable?.address?.[
        this.$i18n.locale
      ];
    },
    async updateNotes(notes) {
      this.updatedReservation.notes = notes;
    },
    async updateInvoices(data) {
      this.updatedReservation.invoices = data;
      this.reservation.invoices = data;
      this.$emit("update-reservation", this.reservation);
    },
    async editReservation() {
      this.loading = true;

      const cleanedReservation = JSON.parse(
        JSON.stringify(this.updatedReservation)
      );

      if (Array.isArray(cleanedReservation.invoices)) {
        cleanedReservation.invoices = cleanedReservation.invoices.map(
          invoice => {
            const { data, ...rest } = invoice;
            return rest;
          }
        );
      }

      this.updatedReservation = {
        ...cleanedReservation,
        id: this.reservation.id,
        branchId: this.branchId,
        businessId: this.businessId
      };

      await submitAndNotify(
        this,
        updateReservation(this.updatedReservation),
        reservation => {
          this.loading = false;
          this.editMode = false;
          this.$emit("closeModal");
        }
      );
    },
    updatedDates(data) {
      this.updatedReservation.from = moment(data[0]).format("YYYY-MM-DDTHH:mm");
      this.updatedReservation.to = moment(data[1])
        .endOf("day")
        .format("YYYY-MM-DDTHH:mm");
    },
    async getReservableMedia() {
      this.reservable = (await getItemData({businessId: this.businessId}, this.reservation.data?.reservable?.id));
    }
  }
};
</script>

<style lang="css"></style>
